/**
 * Modal para gestionar TrendCategories
 * Permite crear, editar y eliminar categorías de Google Trends
 */

import React, { useState, useEffect } from 'react';
import { FiX, FiPlus, FiEdit2, FiTrash2, FiSave, FiTag, FiDownload } from 'react-icons/fi';
import trendsService from '../services/trendsService';
import './CategoryManagementModal.css';

import Loading from './Loading';
const CategoryManagementModal = ({ isOpen, onClose, onCategoriesUpdated }) => {
  const [categories, setCategories] = useState([]);
  const [googleCategories, setGoogleCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGoogle, setLoadingGoogle] = useState(false);
  const [showGoogleCategories, setShowGoogleCategories] = useState(false);
  const [fetchingTrendsId, setFetchingTrendsId] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    keywords: '',
    is_active: true,
  });

  useEffect(() => {
    if (isOpen) {
      loadCategories();
      loadGoogleCategories();
    }
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await trendsService.getCategories();
      setCategories(data.results || data);
    } catch (error) {
      console.error('Error loading categories:', error);
      alert('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  const loadGoogleCategories = async () => {
    try {
      setLoadingGoogle(true);
      const data = await trendsService.getGoogleTrendsCategories();
      setGoogleCategories(data.categories || []);
    } catch (error) {
      console.error('Error loading Google categories:', error);
    } finally {
      setLoadingGoogle(false);
    }
  };

  const handleUseGoogleCategory = (googleCat) => {
    setFormData({
      name: googleCat.name,
      keywords: '',  // User will need to add keywords
      is_active: true,
    });
    setShowGoogleCategories(false);
    alert(`📋 Categoría "${googleCat.name}" seleccionada. Ahora agrega las keywords que quieres trackear para esta categoría.`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('El nombre de la categoría es requerido');
      return;
    }

    // Convert keywords from string to array
    const keywordsArray = formData.keywords
      .split(',')
      .map(k => k.trim())
      .filter(k => k.length > 0);

    if (keywordsArray.length === 0) {
      alert('Debes agregar al menos una keyword');
      return;
    }

    const categoryData = {
      name: formData.name.trim(),
      keywords: keywordsArray,
      is_active: formData.is_active,
    };

    try {
      if (editingCategory) {
        await trendsService.updateCategory(editingCategory.id, categoryData);
        alert('✅ Categoría actualizada exitosamente');
      } else {
        await trendsService.createCategory(categoryData);
        alert('✅ Categoría creada exitosamente');
      }

      resetForm();
      loadCategories();
      if (onCategoriesUpdated) {
        onCategoriesUpdated();
      }
    } catch (error) {
      console.error('Error saving category:', error);
      const errorMsg = error.response?.data?.name?.[0] || error.message || 'Error desconocido';
      alert(`❌ Error al guardar categoría: ${errorMsg}`);
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      keywords: Array.isArray(category.keywords)
        ? category.keywords.join(', ')
        : category.keywords,
      is_active: category.is_active,
    });
  };

  const handleDelete = async (category) => {
    if (!window.confirm(`¿Estás seguro de eliminar la categoría "${category.name}"?\n\nEsto también eliminará todas las tendencias asociadas.`)) {
      return;
    }

    try {
      await trendsService.deleteCategory(category.id);
      alert('✅ Categoría eliminada exitosamente');
      loadCategories();
      if (onCategoriesUpdated) {
        onCategoriesUpdated();
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('❌ Error al eliminar categoría');
    }
  };

  const handleFetchTrends = async (category) => {
    if (!category.keywords || category.keywords.length === 0) {
      alert('⚠️ Esta categoría no tiene keywords configuradas');
      return;
    }

    setFetchingTrendsId(category.id);

    try {
      const result = await trendsService.fetchTrendsForCategory(category.id);

      if (result.status === 'success') {
        alert(`${result.message}\n\n📊 Tendencias obtenidas: ${result.snapshots_created}\n🔑 Keywords: ${result.keywords.join(', ')}`);
        if (onCategoriesUpdated) {
          onCategoriesUpdated();
        }
      } else {
        alert(`⚠️ ${result.message}`);
      }
    } catch (error) {
      console.error('Error fetching trends:', error);
      const errorMsg = error.response?.data?.message || error.message || 'Error desconocido';
      alert(`❌ Error al obtener tendencias: ${errorMsg}`);
    } finally {
      setFetchingTrendsId(null);
    }
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({
      name: '',
      keywords: '',
      is_active: true,
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content category-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>
            <FiTag /> Gestionar Categorías de Google Trends
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FiX size={24} />
          </button>
        </div>

        <div className="modal-body category-modal-body">
          {/* Form Section */}
          <div className="category-form-section">
            <h3>{editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <label style={{ margin: 0 }}>Nombre de la Categoría *</label>
                  <button
                    type="button"
                    onClick={() => setShowGoogleCategories(!showGoogleCategories)}
                    style={{
                      padding: '0.35rem 0.75rem',
                      fontSize: '0.8rem',
                      background: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem'
                    }}
                  >
                    <FiTag size={14} />
                    {showGoogleCategories ? 'Ocultar' : 'Ver'} Categorías Google
                  </button>
                </div>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="ej: Tecnología, Moda, Food & Lifestyle"
                  required
                />
              </div>

              {/* Google Trends Categories Browser */}
              {showGoogleCategories && (
                <div className="google-categories-browser">
                  <div className="google-header">
                    <strong>Categorías Oficiales de Google Trends</strong>
                    <small>Haz clic en una categoría para usarla</small>
                  </div>
                  {loadingGoogle ? (
                    <Loading fullScreen={false} size="sm" mensaje="Cargando categorías" />
                  ) : (
                    <div className="google-categories-grid">
                      {googleCategories.map((cat) => (
                        <div
                          key={cat.id}
                          className="google-category-item"
                          onClick={() => handleUseGoogleCategory(cat)}
                        >
                          <span className="google-cat-name">{cat.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Keywords (separadas por comas) *</label>
                <textarea
                  value={formData.keywords}
                  onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                  placeholder="ej: smartphone, apps, tech review, gadgets"
                  rows={4}
                  required
                />
                <small>Ingresa las palabras clave que quieres trackear en Google Trends, separadas por comas</small>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  <span>Categoría activa</span>
                </label>
              </div>

              <div className="form-actions">
                {editingCategory && (
                  <button type="button" className="btn-secondary" onClick={resetForm}>
                    Cancelar
                  </button>
                )}
                <button type="submit" className="btn-primary">
                  <FiSave /> {editingCategory ? 'Actualizar' : 'Crear'} Categoría
                </button>
              </div>
            </form>
          </div>

          {/* Categories List Section */}
          <div className="categories-list-section">
            <h3>Categorías Existentes ({categories.length})</h3>

            {loading ? (
              <Loading fullScreen={false} size="sm" mensaje="Cargando categorías" />
            ) : categories.length === 0 ? (
              <div className="empty-state">
                <FiTag size={48} />
                <p>No hay categorías creadas</p>
                <p className="hint">Crea tu primera categoría para comenzar a trackear tendencias</p>
              </div>
            ) : (
              <div className="categories-list">
                {categories.map((category) => (
                  <div key={category.id} className="category-card">
                    <div className="category-header">
                      <h4>{category.name}</h4>
                      <div className="category-actions">
                        <span className={`badge ${category.is_active ? 'active' : 'inactive'}`}>
                          {category.is_active ? 'Activa' : 'Inactiva'}
                        </span>
                        <button
                          className="icon-btn edit"
                          onClick={() => handleEdit(category)}
                          title="Editar"
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          className="icon-btn delete"
                          onClick={() => handleDelete(category)}
                          title="Eliminar"
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="category-keywords">
                      <strong>Keywords ({Array.isArray(category.keywords) ? category.keywords.length : 0}):</strong>
                      <div className="keyword-tags">
                        {(Array.isArray(category.keywords) ? category.keywords : []).map((keyword, idx) => (
                          <span key={idx} className="keyword-tag">
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Fetch Trends Button */}
                    <div style={{ marginTop: '1rem' }}>
                      <button
                        className="estado-btn"
                        onClick={() => handleFetchTrends(category)}
                        disabled={fetchingTrendsId === category.id}
                        style={{
                          width: '100%',
                          padding: '0.75rem',
                          background: fetchingTrendsId === category.id ? '#6c757d' : '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: fetchingTrendsId === category.id ? 'not-allowed' : 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '0.5rem',
                          fontSize: '0.9rem',
                          fontWeight: '500',
                          transition: 'all 0.2s'
                        }}
                      >
                        <FiDownload size={16} className={fetchingTrendsId === category.id ? 'spinning' : ''} />
                        {fetchingTrendsId === category.id ? 'Obteniendo tendencias...' : 'Obtener Tendencias de Google'}
                      </button>
                    </div>

                    {category.created_at && (
                      <div className="category-meta">
                        Creada: {new Date(category.created_at).toLocaleDateString('es-CL')}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryManagementModal;
